import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthDisplayProvider, useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { previewEndpoint, previewLocation, previewNotice, previewNoticeEvent } from '../../services/studentPreview';
import { setUiRedesignAuthContext } from '../../config/uiRedesign';
import { User } from '../../types';

export function StudentPreview({ children }: { children: React.ReactNode }) {
  const actor = useAuth();
  const preview = previewLocation();
  const [student, setStudent] = useState<User | null>(null);
  const [sectionName, setSectionName] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [retry, setRetry] = useState(0);
  const base = preview?.basename;
  useEffect(() => {
    const receive = (event: Event) => setNotice((event as CustomEvent<string>).detail);
    window.addEventListener(previewNoticeEvent, receive);
    return () => window.removeEventListener(previewNoticeEvent, receive);
  }, []);
  useEffect(() => {
    if (!preview || actor.user?.role !== 'teacher') return;
    let active = true;
    setError('');
    api.get(previewEndpoint(preview)).then(({ data }) => {
      if (!active) return;
      setUiRedesignAuthContext({ role: 'student', studentId: data.student.id });
      setSectionName(data.section.name);
      setStudent(data.student);
    }).catch(() => { if (active) setError('This preview could not be opened. The student must be enrolled in a class you teach.'); });
    return () => {
      active = false;
      setUiRedesignAuthContext({ role: actor.user?.role, teacherId: actor.user?.id });
    };
    // The preview is fixed to a URL; normal student navigation retains its basename.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, actor.user?.id, actor.user?.role, retry]);
  if (!preview) return <>{children}</>;
  const returnHref = `/v2/teacher/students?section=${preview.sectionId}`;
  if (actor.loading) return <p role="status" className="p-6">Loading preview…</p>;
  if (actor.user?.role !== 'teacher') return <div className="p-6"><p>Sign in with your teacher account to view a student dashboard.</p><a className="underline" href="/login">Go to sign in</a></div>;
  const name = student ? `${student.first_name} ${student.last_name}`.trim() || student.username : 'student';
  return <div className="student-dashboard-preview">
    <aside className="sticky top-0 z-50 border-b border-amber-300 bg-amber-50 px-4 py-3 text-amber-950" aria-label="Student preview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="font-semibold">Viewing {name}’s dashboard · Read only</p><p className="text-sm">{sectionName ? `${sectionName} · ` : ''}Browse to see where things are. Changes and submissions are disabled.</p></div>
        <a href={returnHref} className="rounded border border-amber-400 px-3 py-2 font-medium">Return to teacher view</a>
      </div>
      {notice && <p role="status" className="mt-2 font-medium">{notice}</p>}
    </aside>
    {error ? <div className="p-6"><p role="alert">{error}</p><button className="btn-secondary mt-3" onClick={() => setRetry(value => value + 1)}>Try again</button></div> : !student ? <p role="status" className="p-6">Loading student dashboard…</p> :
      <AuthDisplayProvider value={{ ...actor, user: student, logout: () => previewNotice() }}>
        {children}
      </AuthDisplayProvider>}
  </div>;
}

// Keep lesson delivery and external tools outside this dashboard-only preview.
export function StudentPreviewPage({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const preview = previewLocation();
  if (!preview) return <>{children}</>;
  const allowed = /^\/v2\/student(?:\/(?:units|coaching|standards|work-review|reviews\/\d+))?$/.test(location.pathname);
  if (!allowed) return <div className="mx-auto max-w-3xl p-6"><h1 className="text-2xl font-semibold">Dashboard preview</h1><p className="my-4">This preview covers the dashboard, goals, progress, and check-ins. Opening lessons or other tools is not available here.</p><a className="btn-secondary" href={`${preview.basename}/v2/student?section=${preview.sectionId}`}>Back to student dashboard</a></div>;
  return <div onClickCapture={event => {
    const anchor = (event.target as HTMLElement).closest('a');
    if (!anchor) return;
    const url = new URL(anchor.href, window.location.origin);
    if (url.origin !== window.location.origin || !url.pathname.startsWith(`${preview.basename}/`)) {
      event.preventDefault(); event.stopPropagation();
      previewNotice('External links and downloads are not opened in this dashboard preview.');
    }
  }}>{children}</div>;
}
